import type { MedplumClient } from '@medplum/core';
import type { Questionnaire } from '@medplum/fhirtypes';

/**
 * Resolve a Questionnaire from a QuestionnaireResponse.questionnaire reference.
 * Handles canonical URLs (with optional |version), Questionnaire/{id}, and bare ids.
 */
export async function resolveQuestionnaireForResponseRef(
  medplum: MedplumClient,
  qref: string,
): Promise<Questionnaire | undefined> {
  if (qref.startsWith('http://') || qref.startsWith('https://')) {
    const urlOnly = qref.includes('|') ? qref.split('|')[0]! : qref;
    const version = qref.includes('|') ? qref.split('|')[1] : undefined;
    try {
      if (version) {
        const versioned = await medplum.searchResources('Questionnaire', {
          url: urlOnly,
          version,
          _count: '1',
        });
        if (versioned[0]) return versioned[0];
      }
      const found = await medplum.searchResources('Questionnaire', {
        url: urlOnly,
        _count: '1',
        _sort: '-_lastUpdated',
      });
      return found[0];
    } catch {
      return undefined;
    }
  }

  const rawId = qref.startsWith('Questionnaire/')
    ? qref.slice('Questionnaire/'.length)
    : qref.includes('/')
      ? qref.split('/').pop() ?? qref
      : qref;
  const id = rawId.split('|')[0];
  if (!id) return undefined;

  try {
    return await medplum.readResource('Questionnaire', id);
  } catch {
    return undefined;
  }
}

export async function readQuestionnaireForResponseRef(
  medplum: MedplumClient,
  qref: string,
): Promise<Questionnaire> {
  medplum.invalidateSearches('Questionnaire');
  const questionnaire = await resolveQuestionnaireForResponseRef(medplum, qref);
  if (!questionnaire) {
    throw new Error('Template not found.');
  }
  return questionnaire;
}
